import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  School as EducationIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { getUserStats } from '../services/userService';
import { useApp, useErrorHandler } from '../contexts/AppContext';

interface StatsCardProps {
  title: string;
  value: number;
  icon: React.ReactElement;
  color: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, icon, color }) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: 2,
              p: 1.5,
              color: 'white'
            }}
          >
            <Box sx={{ fontSize: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {icon}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

interface StatsListProps {
  title: string;
  data: Record<string, number>;
  total: number;
}

const StatsList: React.FC<StatsListProps> = ({ title, data, total }) => {
  const sortedData = Object.entries(data)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10); // 상위 10개만 표시

  const colors = ['#1976d2', '#ed6c02', '#2e7d32', '#9c27b0', '#d32f2f', '#795548', '#607d8b'];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="medium">
          {title}
        </Typography>
        <Box>
          {sortedData.map(([key, value], index) => {
            const percentage = total > 0 ? (value / total * 100).toFixed(1) : '0';
            const color = colors[index % colors.length];
            return (
              <Box key={key} sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {key}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {value}명 ({percentage}%)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#e0e0e0',
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${percentage}%`,
                      height: '100%',
                      backgroundColor: color,
                      borderRadius: 4,
                      transition: 'width 0.5s ease-in-out'
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

interface EducationStatsProps {
  title: string;
  completed: number;
  incomplete: number;
  completionRate: number;
  icon: React.ReactElement;
}

const EducationStatsCard: React.FC<EducationStatsProps> = ({ 
  title, 
  completed, 
  incomplete, 
  completionRate, 
  icon 
}) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight="medium">
            {title}
          </Typography>
          <Box sx={{ color: '#1976d2', fontSize: '1.5rem' }}>
            {icon}
          </Box>
        </Box>
        
        <Box mb={3}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {completionRate.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color="textSecondary">
            전체 이수율
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <CheckIcon sx={{ color: '#2e7d32', mr: 1, fontSize: '1.2rem' }} />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                이수완료
              </Typography>
              <Typography variant="h6" color="success.main">
                {completed}명
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center">
            <CancelIcon sx={{ color: '#d32f2f', mr: 1, fontSize: '1.2rem' }} />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                미이수
              </Typography>
              <Typography variant="h6" color="error.main">
                {incomplete}명
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: 8,
            backgroundColor: '#ffcdd2',
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: `${completionRate}%`,
              height: '100%',
              backgroundColor: '#2e7d32',
              borderRadius: 4,
              transition: 'width 0.8s ease-in-out'
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<{
    total: number;
    byHeadquarters: Record<string, number>;
    byDepartment: Record<string, number>;
    byPosition: Record<string, number>;
    byAssignments: Record<string, number>;
    byLocation: Record<string, number>;
    educationStats: {
      initialEducation: {
        completed: number;
        incomplete: number;
        completionRate: number;
      };
      recentEducation: {
        completed: number;
        incomplete: number;
        completionRate: number;
      };
    };
    monthlyTrends: {
      newRegistrations: Record<string, number>;
      educationCompletions: Record<string, number>;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { state } = useApp();
  const handleError = useErrorHandler();

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const statsData = await getUserStats();
        setStats(statsData);
      } catch (error: any) {
        setError('통계 데이터를 불러오는데 실패했습니다.');
        handleError(error, '대시보드 통계 조회');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [handleError]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        통계 데이터가 없습니다.
      </Alert>
    );
  }

  return (
    <Box>
      {/* 환영 메시지 */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          안녕하세요, {state.user?.displayName || '사용자'}님! 👋
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          보안조직 인원 관리 시스템 대시보드입니다. 
          현재 총 <strong>{stats.total}명</strong>의 인원이 등록되어 있으며, 
          초기교육 <strong>{stats.educationStats.initialEducation.completionRate.toFixed(1)}%</strong>, 
          정기교육 <strong>{stats.educationStats.recentEducation.completionRate.toFixed(1)}%</strong> 이수율을 기록하고 있습니다.
        </Typography>
      </Paper>

      {/* 주요 통계 카드 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <StatsCard
          title="총 인원"
          value={stats.total}
          icon={<PeopleIcon />}
          color="#1976d2"
        />
        <StatsCard
          title="본부 수"
          value={Object.keys(stats.byHeadquarters).length}
          icon={<BusinessIcon />}
          color="#ed6c02"
        />
        <StatsCard
          title="부서 수"
          value={Object.keys(stats.byDepartment).length}
          icon={<WorkIcon />}
          color="#2e7d32"
        />
        <StatsCard
          title="발령 종류"
          value={Object.keys(stats.byAssignments).length}
          icon={<AssignmentIcon />}
          color="#9c27b0"
        />
        <StatsCard
          title="지점/공항"
          value={Object.keys(stats.byLocation).length}
          icon={<LocationIcon />}
          color="#d32f2f"
        />
        <StatsCard
          title="직위 수"
          value={Object.keys(stats.byPosition).length}
          icon={<TrendingUpIcon />}
          color="#795548"
        />
      </Box>

      {/* 교육 이수 현황 */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <EducationIcon sx={{ mr: 1, color: '#1976d2' }} />
        교육 이수 현황
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <EducationStatsCard
          title="초기 보안교육"
          completed={stats.educationStats.initialEducation.completed}
          incomplete={stats.educationStats.initialEducation.incomplete}
          completionRate={stats.educationStats.initialEducation.completionRate}
          icon={<EducationIcon />}
        />
        <EducationStatsCard
          title="정기 보안교육"
          completed={stats.educationStats.recentEducation.completed}
          incomplete={stats.educationStats.recentEducation.incomplete}
          completionRate={stats.educationStats.recentEducation.completionRate}
          icon={<TimelineIcon />}
        />
      </Box>

      {/* 발령사항 & 지점 분석 */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <PieChartIcon sx={{ mr: 1, color: '#1976d2' }} />
        발령사항 & 지점 분석
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <StatsList
          title="📋 발령사항별 인원"
          data={stats.byAssignments}
          total={stats.total}
        />
        <StatsList
          title="📍 지점/공항별 인원"
          data={stats.byLocation}
          total={stats.total}
        />
      </Box>

      {/* 조직 구성 분석 */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <BarChartIcon sx={{ mr: 1, color: '#1976d2' }} />
        조직 구성 분석
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <StatsList
          title="🏢 본부별 인원"
          data={stats.byHeadquarters}
          total={stats.total}
        />
        <StatsList
          title="🏬 부서별 인원"
          data={stats.byDepartment}
          total={stats.total}
        />
        <StatsList
          title="👔 직위별 인원"
          data={stats.byPosition}
          total={stats.total}
        />
      </Box>

      {/* 월별 트렌드 (간단 버전) */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
        <TimelineIcon sx={{ mr: 1, color: '#1976d2' }} />
        월별 등록 현황
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📊 최근 6개월 등록 현황
          </Typography>
          <Box>
            {Object.entries(stats.monthlyTrends.newRegistrations)
              .sort(([a], [b]) => b.localeCompare(a))
              .slice(0, 6)
              .map(([month, count]) => {
                const maxCount = Math.max(...Object.values(stats.monthlyTrends.newRegistrations));
                const percentage = maxCount > 0 ? (count / maxCount * 100) : 0;
                return (
                  <Box key={month} sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2" fontWeight="medium">
                        {month}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {count}명
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 6,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${percentage}%`,
                          height: '100%',
                          backgroundColor: '#1976d2',
                          borderRadius: 3,
                          transition: 'width 0.5s ease-in-out'
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </CardContent>
      </Card>

      {/* 개선된 도움말 */}
      <Paper sx={{ p: 3, mt: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          💡 주요 기능 안내
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <Typography variant="body2" paragraph sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <AssignmentIcon sx={{ mr: 1, mt: 0.2, fontSize: '1rem', color: '#1976d2' }} />
              <Box>
                <strong>발령사항 분석:</strong> 각 발령사항별 인원 분포를 실시간으로 확인하고 
                조직 내 역할 배분을 효율적으로 관리할 수 있습니다.
              </Box>
            </Typography>
            <Typography variant="body2" paragraph sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <LocationIcon sx={{ mr: 1, mt: 0.2, fontSize: '1rem', color: '#ed6c02' }} />
              <Box>
                <strong>지점/공항 관리:</strong> 각 지점별 인원 현황과 교육 이수율을 
                한눈에 파악하여 효과적인 인력 배치를 계획할 수 있습니다.
              </Box>
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" paragraph sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <EducationIcon sx={{ mr: 1, mt: 0.2, fontSize: '1rem', color: '#2e7d32' }} />
              <Box>
                <strong>교육 현황 모니터링:</strong> 초기/정기 보안교육 이수율을 
                실시간으로 추적하고 미이수자 관리를 체계적으로 진행할 수 있습니다.
              </Box>
            </Typography>
            <Typography variant="body2" paragraph sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <TimelineIcon sx={{ mr: 1, mt: 0.2, fontSize: '1rem', color: '#9c27b0' }} />
              <Box>
                <strong>트렌드 분석:</strong> 월별 등록 현황과 교육 완료 트렌드를 
                분석하여 조직 성장과 교육 계획을 수립할 수 있습니다.
              </Box>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;
